import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import { Box, ListItemButton } from '@mui/material';

import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { getPersonFullName } from '@occc-app/lib/person';
import config from '@occc/config';

import {
  Typography,
  Divider,
  LetterAvatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
} from '@occc/ui-design-system';
import { ExitToAppIcon, GroupIcon } from '@occc/ui-design-system/icons';
import { userRoleMap } from './constants';
import { PreparedUser } from './types';
import ChainsDropdown from './ChainsDropdown';

type MyAccountPopoverProps = {
  logout: () => void;
  handleMenuClose: () => void;
  isMenuOpen: boolean;
  menuAnchorEl: HTMLElement | null;
  user: PreparedUser | null;
};

type AppVersionProps = {
  version: string;
};

const AppVersion = ({ version }: AppVersionProps) => (
  <>
    <Divider />
    <Box py={1} px={2}>
      <Typography variant='caption' color='textSecondary'>
        {version}
      </Typography>
    </Box>
  </>
);

export const MyAccountPopover = ({
  menuAnchorEl,
  handleMenuClose,
  isMenuOpen,
  user,
  logout,
}: MyAccountPopoverProps) => {
  const isUserPermittedToViewUserManagement = useIsUserPermittedTo(Feature.USER_MANAGEMENT_VIEW);

  const onUserManagementClick = () => {
    handleMenuClose();
  };

  const userDisplayName = useMemo(() => {
    const userFullName = getPersonFullName(user?.name);
    return userFullName || user?.email || '';
  }, [user?.name, user?.email]);

  return (
    <Popover
      anchorEl={menuAnchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <List component='div' sx={{ width: 264 }}>
        <ListItem component='div' divider>
          <ListItemAvatar>
            <LetterAvatar>{user?.avatarLetters}</LetterAvatar>
          </ListItemAvatar>
          <ListItemText
            primary={<Typography noWrap>{userDisplayName}</Typography>}
            secondary={user?.userRole ? userRoleMap[user.userRole] : ''}
          />
        </ListItem>
        {!config.isLiteVersion && (
          <>
            <ChainsDropdown />
            <Divider />
          </>
        )}

        {isUserPermittedToViewUserManagement && (
          <ListItemButton
            component={Link}
            to='/users'
            onClick={onUserManagementClick}
            data-testid='userManagement'
          >
            <ListItemIcon>
              <GroupIcon />
            </ListItemIcon>
            <ListItemText primary='User Management' />
          </ListItemButton>
        )}
        <ListItemButton onClick={logout} data-testid='logout'>
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary='Log Out' />
        </ListItemButton>
      </List>
      {config.release && <AppVersion version={config.release} />}
    </Popover>
  );
};
